import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import JsonLd from '../../components/base/JsonLd';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    insurance: '',
    urgency: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://celebrity-homehealth.com';

  const contactJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      '@id': `${siteUrl}/contact#webpage`,
      url: `${siteUrl}/contact`,
      name: 'Contact Celebrity Home Health Las Vegas | Request Home Care',
      description: 'Contact Celebrity Home Health in Las Vegas, NV. Request skilled nursing, therapy, or home health aide services. Call 866-939-0330 or fill out our care request form.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Contact', item: `${siteUrl}/contact` },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${siteUrl}/#localbusiness`,
      name: 'Celebrity Home Health',
      url: siteUrl,
      telephone: '+18669390330',
      email: 'info@celebrity-homehealth.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '2121 E Flamingo Rd Ste 218A',
        addressLocality: 'Las Vegas',
        addressRegion: 'NV',
        postalCode: '89119',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '36.1037',
        longitude: '-115.1481',
      },
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '08:00', closes: '17:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '09:00', closes: '14:00' },
      ],
      areaServed: ['Las Vegas', 'Henderson', 'North Las Vegas', 'Summerlin', 'Green Valley'],
      hasMap: 'https://www.google.com/maps?q=2121+E+Flamingo+Rd+Ste+218A+Las+Vegas+NV+89119',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${siteUrl}/contact#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Do you accept Medicare?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, we are Medicare certified and accept most major insurance plans including Medicare, Medicaid, and private insurance.',
          },
        },
        {
          '@type': 'Question',
          name: 'How quickly can care start?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'We can often begin care within 24–48 hours of your initial consultation, depending on your needs and insurance approval.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do I need a doctor\'s referral?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes, home health care requires a physician\'s order. We can coordinate with your doctor to obtain the necessary referral.',
          },
        },
        {
          '@type': 'Question',
          name: 'What areas do you serve?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'We serve the entire Las Vegas Valley including Las Vegas, Henderson, North Las Vegas, Summerlin, Green Valley, and surrounding areas.',
          },
        },
      ],
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => params.append(k, v));
      const response = await fetch('https://readdy.ai/api/form/d3c52mtjjqdr4q69hqri', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitStatus(response.ok ? 'success' : 'error');
      if (response.ok) {
        setFormData({ name: '', email: '', phone: '', serviceType: '', insurance: '', urgency: '', message: '' });
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const faqs = [
    { q: 'Do you accept Medicare?', a: 'Yes, we are Medicare certified and accept most major insurance plans including Medicare, Medicaid, and private insurance.' },
    { q: 'How quickly can care start?', a: 'We can often begin care within 24–48 hours of your initial consultation, depending on your needs and insurance approval.' },
    { q: 'Do I need a doctor\'s referral?', a: 'Yes, home health care requires a physician\'s order. We can coordinate with your doctor to obtain the necessary referral.' },
    { q: 'What areas do you serve?', a: 'We serve the entire Las Vegas Valley including Las Vegas, Henderson, North Las Vegas, Summerlin, Green Valley, and surrounding areas.' },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <JsonLd data={contactJsonLd as Record<string, unknown>[]} id="json-ld-contact" />
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/bf336160cef9741efa09d9df981555c0.jpg"
            alt="Contact Celebrity Home Health"
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/85 to-slate-900/98"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 border border-amber-400/30 rounded-full text-amber-300 text-xs font-semibold uppercase tracking-wider mb-6">
            <i className="ri-customer-service-line"></i> Contact Us
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-5">We're Here to Help</h1>
          <p className="text-slate-300 text-xl max-w-2xl mx-auto">
            Have questions or ready to start care? Our team is available to answer your questions and help you get started.
          </p>
        </div>
      </section>

      {/* NO SOLICITATION POLICY — TOP LEVEL BANNER */}
      <section className="bg-red-950 border-b-4 border-red-700">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-red-700 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-forbid-2-fill text-white text-xl"></i>
            </div>
            <div className="flex-1">
              <p className="text-white font-black text-sm uppercase tracking-widest mb-2">Strict No Solicitation Policy — Read Before Contacting</p>
              <p className="text-red-200 text-sm leading-relaxed mb-3">
                This website and all associated businesses <strong className="text-white">strictly prohibit</strong> any unsolicited communications — including sales, marketing, lead generation, promotional offers, partnerships, or outreach of any kind. <strong className="text-white">Any such submission will be permanently blocked without response.</strong>
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <p className="text-red-300 text-xs font-bold uppercase tracking-wider mb-1.5">This channel is reserved for:</p>
                  <ul className="space-y-1">
                    {['Clients and patients seeking services', 'Authorized referral sources', 'Verified business matters only'].map((item) => (
                      <li key={item} className="flex items-center gap-2 text-red-100 text-xs">
                        <i className="ri-checkbox-circle-fill text-red-400 flex-shrink-0"></i>{item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-red-300 text-xs font-bold uppercase tracking-wider mb-1.5">By submitting you agree that:</p>
                  <ul className="space-y-1">
                    {['Unsolicited messages are strictly prohibited', 'Your info may be blocked and reported as spam', 'Violations may result in legal action & blacklisting'].map((item) => (
                      <li key={item} className="flex items-center gap-2 text-red-100 text-xs">
                        <i className="ri-close-circle-fill text-red-500 flex-shrink-0"></i>{item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="bg-red-900/70 border border-red-700 rounded-lg px-4 py-2.5">
                <p className="text-white text-xs font-black uppercase tracking-wide text-center">
                  We do not engage with cold outreach under any circumstances. Continued disregard constitutes intentional interference with business operations. Proceed only if your inquiry is legitimate.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-6">
            <a href="tel:866-939-0330" className="group bg-white p-8 rounded-2xl border border-stone-200 hover:border-amber-300 hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-amber-200 transition-colors duration-300">
                <i className="ri-phone-line text-amber-700 text-xl"></i>
              </div>
              <h3 className="font-bold text-slate-900 text-lg mb-2">Call Us</h3>
              <p className="text-slate-500 text-sm mb-3">Available 24/7 for urgent support</p>
              <p className="text-amber-600 font-bold text-lg">866-939-0330</p>
            </a>

            <a href="mailto:info@celebrity-homehealth.com" className="group bg-white p-8 rounded-2xl border border-stone-200 hover:border-amber-300 hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-amber-200 transition-colors duration-300">
                <i className="ri-mail-line text-amber-700 text-xl"></i>
              </div>
              <h3 className="font-bold text-slate-900 text-lg mb-2">Email Us</h3>
              <p className="text-slate-500 text-sm mb-3">We respond within 24 hours</p>
              <p className="text-amber-600 font-semibold text-sm">info@celebrity-homehealth.com</p>
            </a>

            <div className="bg-white p-8 rounded-2xl border border-stone-200">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-5">
                <i className="ri-map-pin-line text-amber-700 text-xl"></i>
              </div>
              <h3 className="font-bold text-slate-900 text-lg mb-2">Visit Us</h3>
              <p className="text-slate-500 text-sm mb-1">2121 E Flamingo Rd Ste 218A</p>
              <p className="text-slate-500 text-sm">Las Vegas, NV 89119</p>
              <div className="mt-4 pt-4 border-t border-stone-100">
                <p className="text-slate-600 text-sm font-medium">Mon–Fri: 8AM–5PM</p>
                <p className="text-slate-600 text-sm">Sat: 9AM–2PM · Sun: Closed</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form + Info */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-16">
            {/* Form */}
            <div className="lg:col-span-3">

              <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-5">
                Request Care
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-3">Get a Free Consultation</h2>
              <p className="text-slate-500 mb-8">Fill out the form and our care coordinator will contact you within 24 hours to discuss your needs.</p>

              <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-slate-800 mb-1.5">Full Name *</label>
                  <input
                    type="text" id="name" name="name" value={formData.name}
                    onChange={handleChange} required placeholder="John Doe"
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-200 text-slate-900 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 placeholder-slate-400"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-slate-800 mb-1.5">Email Address *</label>
                    <input
                      type="email" id="email" name="email" value={formData.email}
                      onChange={handleChange} required placeholder="john@example.com"
                      className="w-full px-4 py-3 bg-stone-50 border border-stone-200 text-slate-900 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 placeholder-slate-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-slate-800 mb-1.5">Phone Number *</label>
                    <input
                      type="tel" id="phone" name="phone" value={formData.phone}
                      onChange={handleChange} required placeholder="(702) 555-0123"
                      className="w-full px-4 py-3 bg-stone-50 border border-stone-200 text-slate-900 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 placeholder-slate-400"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="serviceType" className="block text-sm font-semibold text-slate-800 mb-1.5">Service Needed *</label>
                  <select
                    id="serviceType" name="serviceType" value={formData.serviceType}
                    onChange={handleChange} required
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-200 text-slate-900 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent cursor-pointer"
                  >
                    <option value="">Select a service</option>
                    <option value="Skilled Nursing">Skilled Nursing</option>
                    <option value="Physical Therapy">Physical Therapy</option>
                    <option value="Occupational Therapy">Occupational Therapy</option>
                    <option value="Speech Therapy">Speech Therapy</option>
                    <option value="Medical Social Services">Medical Social Services</option>
                    <option value="Home Health Aide">Home Health Aide</option>
                    <option value="Not Sure">Not Sure — I need guidance</option>
                  </select>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="insurance" className="block text-sm font-semibold text-slate-800 mb-1.5">Insurance Provider</label>
                    <input
                      type="text" id="insurance" name="insurance" value={formData.insurance}
                      onChange={handleChange} placeholder="Medicare, Medicaid, etc."
                      className="w-full px-4 py-3 bg-stone-50 border border-stone-200 text-slate-900 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent placeholder-slate-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="urgency" className="block text-sm font-semibold text-slate-800 mb-1.5">When Do You Need Care? *</label>
                    <select
                      id="urgency" name="urgency" value={formData.urgency}
                      onChange={handleChange} required
                      className="w-full px-4 py-3 bg-stone-50 border border-stone-200 text-slate-900 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent cursor-pointer"
                    >
                      <option value="">Select timeframe</option>
                      <option value="Immediate (within 24 hours)">Immediate (within 24 hours)</option>
                      <option value="Within a week">Within a week</option>
                      <option value="Within a month">Within a month</option>
                      <option value="Just exploring options">Just exploring options</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-slate-800 mb-1.5">Additional Information</label>
                  <textarea
                    id="message" name="message" value={formData.message}
                    onChange={handleChange} rows={4} maxLength={500}
                    placeholder="Tell us about your care needs... (Max 500 characters)"
                    className="w-full px-4 py-3 bg-stone-50 border border-stone-200 text-slate-900 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none placeholder-slate-400"
                  />
                  <p className={`text-xs mt-1 text-right ${formData.message.length > 480 ? 'text-amber-600' : 'text-slate-400'}`}>
                    {formData.message.length}/500
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl">
                    <i className="ri-checkbox-circle-fill text-xl"></i>
                    <span className="text-sm font-medium">Request submitted! We'll contact you within 24 hours.</span>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 text-red-800 rounded-xl">
                    <i className="ri-error-warning-fill text-xl"></i>
                    <span className="text-sm font-medium">Something went wrong. Please try again or call us at 866-939-0330.</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || formData.message.length > 500}
                  className="w-full px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i> Submitting...
                    </span>
                  ) : 'Submit Care Request'}
                </button>
              </form>
            </div>

            {/* Side info */}
            <div className="lg:col-span-2 space-y-8">
              {/* Service Areas */}
              <div className="bg-stone-50 p-7 rounded-2xl border border-stone-200">
                <h3 className="font-bold text-slate-900 text-lg mb-4 flex items-center gap-2">
                  <i className="ri-map-pin-2-line text-amber-600"></i>
                  Service Areas
                </h3>
                <div className="space-y-2">
                  {['Las Vegas', 'Henderson', 'North Las Vegas', 'Summerlin', 'Green Valley', 'Enterprise', 'Spring Valley', 'Paradise'].map((area) => (
                    <div key={area} className="flex items-center gap-2 text-slate-600 text-sm">
                      <i className="ri-checkbox-circle-fill text-amber-500 text-base"></i>
                      {area}
                    </div>
                  ))}
                </div>
              </div>

              {/* FAQ */}
              <div>
                <h3 className="font-bold text-slate-900 text-lg mb-4">Frequently Asked Questions</h3>
                <div className="space-y-3">
                  {faqs.map((faq, i) => (
                    <div key={i} className="bg-white p-5 rounded-xl border border-stone-200">
                      <h4 className="font-semibold text-slate-900 text-sm mb-2">{faq.q}</h4>
                      <p className="text-slate-500 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Visit Our Office</h2>
            <p className="text-slate-500">2121 E Flamingo Rd Ste 218A, Las Vegas, NV 89119</p>
          </div>

          <div className="w-full h-[320px] rounded-2xl overflow-hidden border border-stone-200 mb-6">
            <img
              src="https://storage.readdy-site.link/project_files/2271c221-4028-4954-ba67-e2b66eec11ce/746a6434-68f7-47e8-8af0-1ba0e8d7467c_Med-Care-Celebrity-and-Advantixx-Office.png?v=8237b076c97d520d9460ffea8f33b8ca"
              alt="Celebrity Home Health Office - 2121 E Flamingo Rd, Las Vegas, NV"
              className="w-full h-full object-cover object-center"
            />
          </div>

          <div className="w-full h-[450px] rounded-2xl overflow-hidden border border-stone-200">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3223.8!2d-115.1!3d36.1!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzbCsDA2JzAwLjAiTiAxMTXCsDA2JzAwLjAiVw!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Celebrity Home Health Location"
            ></iframe>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
